import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { Customer } from '../../common/Customer';
import { RootState, User } from '../../shared/store';

export type CPCustomer = {
  name: string;
  marketId: string;
  customerId: string;
  customerUsers: string[];
  am_email?: string;
  marketCountryName: string;
  erpId?: string;
};

export type RawCPCustomerUser = {
  id: string;
  attributes: {
    firstName: string;
    lastName: string;
    email: string;
    e_lab_administrator: boolean;
    GUID: string;
    invoice_guid: string;
  }
}

export type CPCustomerUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  erpId?: string;
  customerName?: string;
  e_lab_administrator: boolean;
  GUID?: string | null;
  invoice_guid?: string | null;
};

export type MarketSegment = {
  marketId: string;
};

export type CustomerState = {
  /**
   * Customer object, retrieved from CP endpoint
   */
  cpCustomer: CPCustomer | null;
  /**
   * Customer object, retrieved from Database
   */
  customer: Customer | null;
  /**
   * ERP ID for the selected customer
   */
  customerId: string | null;
  /**
   * Retrieved from CP endpoint
   */
  cpCustomerUser: CPCustomerUser | null;
  /**
   * Created in or retrieved from firebase authentication
   */
  customerUser: User | null;
  /**
   * Indicates if the user has chosen a customerUser or cancelled the modal
   */
  isSelectionConfirmed: boolean;
};

const initialState: CustomerState = {
  cpCustomer: null,
  customer: null,
  customerId: null,
  cpCustomerUser: null,
  customerUser: null,
  isSelectionConfirmed: false,
};

export const state = createSlice({
  name: 'customerState',
  initialState,
  reducers: {
    resetAll: () => initialState,
    setCPCustomer: (
      state: CustomerState,
      action: PayloadAction<CPCustomer | null>,
    ) => {
      state.cpCustomer = action.payload;
    },
    setCustomerId: (
      state: CustomerState,
      action: PayloadAction<string | null>,
    ) => {
      state.customerId = action.payload;
    },
    setCustomer: (
      state: CustomerState,
      action: PayloadAction<Customer | null>,
    ) => {
      state.customer = action.payload;
    },
    setCPCustomerUser: (
      state: CustomerState,
      action: PayloadAction<CPCustomerUser | null>,
    ) => {
      state.cpCustomerUser = action.payload;
    },
    setCustomerUser: (
      state: CustomerState,
      action: PayloadAction<User | null>,
    ) => {
      state.customerUser = action.payload;
    },
    setIsSelectionConfirmed: (
      state: CustomerState,
      action: PayloadAction<boolean>,
    ) => {
      state.isSelectionConfirmed = action.payload;
    },
  },
});

export const useCustomerState = (): [CustomerState, typeof state.actions] => {
  return [useSelector((state: RootState) => state.customerInfo), state.actions];
};

export const actions = state.actions;

export const reducers = state.reducer;
